// src/features/records/components/GalleryView/layout/computeJustifiedRows.js

const DEFAULT_OPTIONS = {
  gap: 4,
  targetRowHeight: 200,
  minScale: 0.75,
  maxScale: 1.35,
  justifyLastRow: false,
  widowLimit: 2,
};

function readAspectRatio(media) {
  const width =
    media.width ??
    media.meta?.width ??
    media.metadata?.width ??
    media.dimensions?.width ??
    media.posterDimensions?.width ??
    null;
  const height =
    media.height ??
    media.meta?.height ??
    media.metadata?.height ??
    media.dimensions?.height ??
    media.posterDimensions?.height ??
    null;

  const ratio = Number(width) > 0 && Number(height) > 0 ? Number(width) / Number(height) : null;
  if (ratio && Number.isFinite(ratio) && ratio > 0) {
    return ratio;
  }

  const fallback = Number(media.aspectRatio);
  if (fallback && Number.isFinite(fallback) && fallback > 0) {
    return fallback;
  }

  return 1;
}

function normalizeItems(items) {
  return items.map((item, index) => ({
    item,
    aspectRatio: Math.max(0.01, readAspectRatio(item)),
    index,
  }));
}

function distributeWidths(entries, availableWidth, height) {
  if (entries.length === 0) {
    return [];
  }

  const widths = [];
  const fractions = [];
  let used = 0;

  entries.forEach((entry, idx) => {
    const raw = height * entry.aspectRatio;
    const base = Math.floor(raw);
    widths[idx] = base;
    used += base;
    fractions[idx] = { idx, frac: raw - base };
  });

  let remainder = Math.round(availableWidth - used);
  if (remainder > 0) {
    fractions
      .slice()
      .sort((a, b) => b.frac - a.frac)
      .forEach(({ idx }) => {
        if (remainder <= 0) return;
        widths[idx] += 1;
        remainder -= 1;
      });
  } else if (remainder < 0) {
    fractions
      .slice()
      .sort((a, b) => a.frac - b.frac)
      .forEach(({ idx }) => {
        if (remainder >= 0) return;
        if (widths[idx] > 1) {
          widths[idx] -= 1;
          remainder += 1;
        }
      });
  }

  return widths.map((width) => Math.max(1, width));
}

function buildRow({ entries, containerWidth, gap, key, height, targetWidth, justify }) {
  const notionalWidth = Math.max(0, targetWidth);
  const totalGap = gap * Math.max(0, entries.length - 1);
  const pixelWidths = distributeWidths(entries, notionalWidth, height);

  let consumed = 0;
  const items = entries.map((entry, idx) => {
    const width = pixelWidths[idx];
    consumed += width;
    return {
      key: entry.item.id ?? `idx-${entry.index}`,
      width,
      height,
      item: entry.item,
    };
  });

  return {
    key,
    height,
    rowWidth: consumed + totalGap,
    items,
    justify,
  };
}

function createJustifiedRow(entries, containerWidth, gap, key) {
  const totalGap = gap * Math.max(0, entries.length - 1);
  const arSum = entries.reduce((sum, entry) => sum + entry.aspectRatio, 0);
  const availableWidth = Math.max(0, containerWidth - totalGap);
  const heightBase = availableWidth > 0 && arSum > 0 ? availableWidth / arSum : 0;
  const height = Math.max(1, heightBase);

  return buildRow({
    key,
    entries,
    containerWidth,
    gap,
    height,
    targetWidth: availableWidth,
    justify: true,
  });
}

function createRaggedRow(entries, containerWidth, options, key) {
  const { gap, targetRowHeight, maxScale } = options;
  const totalGap = gap * Math.max(0, entries.length - 1);
  const arSum = entries.reduce((sum, entry) => sum + entry.aspectRatio, 0);
  const availableWidth = Math.max(0, containerWidth - totalGap);
  const fillHeight = arSum > 0 ? availableWidth / arSum : 0;
  const maxHeight = targetRowHeight * maxScale;
  const safeFillHeight = fillHeight > 0 ? fillHeight : targetRowHeight;
  const height = Math.max(1, Math.min(targetRowHeight, maxHeight, safeFillHeight));
  const targetWidth = Math.max(0, Math.round(height * arSum));

  return buildRow({
    key,
    entries,
    containerWidth,
    gap,
    height,
    targetWidth,
    justify: false,
  });
}

export function computeJustifiedRows(items, containerWidth, options = {}) {
  const width = Math.max(0, containerWidth || 0);
  const { gap, targetRowHeight, minScale, maxScale, justifyLastRow, widowLimit } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  if (!Array.isArray(items) || items.length === 0 || width === 0) {
    return [];
  }

  const normalized = normalizeItems(items);
  const rows = [];
  let currentRow = [];
  let arSum = 0;
  const minHeight = targetRowHeight * minScale;
  const maxHeight = targetRowHeight * maxScale;

  normalized.forEach((entry) => {
    currentRow.push(entry);
    arSum += entry.aspectRatio;

    const totalGap = gap * Math.max(0, currentRow.length - 1);
    const availableWidth = Math.max(0, width - totalGap);
    const rowHeight = arSum > 0 ? availableWidth / arSum : targetRowHeight;

    if (rowHeight < minHeight && currentRow.length > 1) {
      const last = currentRow.pop();
      arSum -= last.aspectRatio;
      if (currentRow.length) {
        const rowKey = `row-${rows.length}`;
        rows.push(createJustifiedRow(currentRow.slice(), width, gap, rowKey));
      }
      currentRow = [last];
      arSum = last.aspectRatio;
      return;
    }

    if (rowHeight <= maxHeight) {
      const rowKey = `row-${rows.length}`;
      rows.push(createJustifiedRow(currentRow.slice(), width, gap, rowKey));
      currentRow = [];
      arSum = 0;
    }
  });

  if (currentRow.length) {
    const rowKey = `row-${rows.length}`;
    const shouldJustify = justifyLastRow && currentRow.length >= widowLimit;
    if (shouldJustify) {
      rows.push(createJustifiedRow(currentRow, width, gap, rowKey));
    } else {
      rows.push(createRaggedRow(currentRow, width, { gap, targetRowHeight, maxScale }, rowKey));
    }
  }

  return rows;
}

export default computeJustifiedRows;
